"use client"
import { useState, useEffect } from "react"
import {
  ChevronLeft,
  ChevronRight,
  Home,
  Package,
  Palette,
  Ruler,
  Plus,
  ShoppingCart,
  CreditCard,
  FileText,
  BarChart3,
  LogOut,
  User,
  LayoutDashboard,
  Tag,
  ShoppingBag,
} from "lucide-react"
import { Link, useNavigate } from "react-router-dom"

// Navigation data structure
const navigationData = {
  main: [
    {
      title: "Dashboard",
      url: "/dashboard",
      icon: LayoutDashboard,
    },
    {
      title: "Products",
      url: "/all-products",
      icon: Package,
    },
   
    {
      title: "Purchases",
      url: "/all-purchases",
      icon: ShoppingCart,
    },
    {
      title: "Sales",
      url: "/all-sales",
      icon: ShoppingBag,
    },
    {
      title: "Inventory",
      url: "/inventory",
      icon: Package,
    },
    {
      title: "Users",
      url: "/all-users",
      icon: User,
    },
  ],
  manageInventory: [
    {
      title: "All Products",
      url: "/all-products",
      icon: Package,
    },
    {
      title: "Product Category",
      url: "/product-category",
      icon: Package,
    },
    {
      title: "Product Colours",
      url: "/product-color",
      icon: Palette,
    },
    {
      title: "Product Units",
      url: "/product-units",
      icon: Ruler,
    },
    {
      title: "Add Product",
      url: "/add-product",
      icon: Plus,
    },
  ],
  sales: [
    {
      title: "All Sales",
      url: "/all-sales",
      icon: ShoppingCart,
    },
    {
      title: "Add Sales",
      url: "/add-sale",
      icon: Plus,
    },
  
  ],
  purchases: [
    {
      title: "All Purchases",
      url: "/all-purchases",
      icon: CreditCard,
    },
    {
      title: "Add Purchase",
      url: "/add-purchase",
      icon: Plus,
    },
   
  ],
  reports: [
    {
      title: "Sales Report",
      url: "/sales-report",
      icon: BarChart3,
    },
    {
      title: "Purchase Report",
      url: "/purchase-report",
      icon: BarChart3,
    },
    {
      title: "Inventory Report",
      url: "/inventory-report",
      icon: BarChart3,
    },
  ],
  users: [
    {
      title: "All Users",
      url: "/all-users",
      icon: User,
    },
  ],
  settings: [
    {
      title: "Logout",
      type: 'btn',
      url: "/logout",
      icon: LogOut,
    },
  ],
}

const SideNavbar = ({ isCollapsed, setIsCollapsed }) => {
  const [activeItem, setActiveItem] = useState("/dashboard")
  const navigate = useNavigate()

  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken")
    if (!accessToken) {
      navigate("/login")
    }
  }, [navigate])

  const handleLogout = () => {
    localStorage.removeItem("accessToken")
    navigate("/login")
  }

  return (
    <aside
      className={`fixed top-0 left-0 h-screen bg-white shadow-lg flex flex-col z-50 transition-all duration-300 ${
        isCollapsed ? "w-16" : "w-64"
      }`}
    >
      {/* Toggle Button */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -right-3 top-6 bg-orange-500 text-white rounded-full p-1 shadow-lg hover:bg-orange-600 transition-colors z-10"
      >
        {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
      </button>

      {/* Logo/Header */}
      <div className={`flex items-center gap-2 px-4 py-4 border-b ${isCollapsed ? "justify-center" : ""}`}>
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-orange-500 text-white flex-shrink-0">
          <Package className="h-4 w-4" />
        </div>
        {!isCollapsed && (
          <span className="font-semibold text-lg whitespace-nowrap overflow-hidden">PREMIER LAMINATES</span>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        {/* Main Section */}
        {!isCollapsed && <div className="mb-2 px-4 text-xs font-bold text-gray-500">Main</div>}
        {navigationData.main.map((item) => (
          <Link
            key={item.title}
            to={item.url}
            className={`flex items-center gap-2 py-2 hover:bg-orange-100 transition-colors text-sm group relative ${
              activeItem === item.url ? "bg-orange-50 text-orange-600 font-semibold" : "text-gray-700"
            } ${isCollapsed ? "px-4 justify-center" : "px-6"}`}
            onClick={() => setActiveItem(item.url)}
            title={isCollapsed ? item.title : ""}
          >
            <item.icon className="h-4 w-4 flex-shrink-0" />
            {!isCollapsed && <span className="whitespace-nowrap">{item.title}</span>}
            {isCollapsed && (
              <div className="absolute left-full ml-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                {item.title}
              </div>
            )}
          </Link>
        ))}

        {/* Manage Inventory Section */}
        {!isCollapsed && <div className="mt-4 mb-2 px-4 text-xs font-bold text-gray-500">Manage Inventory</div>}
        {navigationData.manageInventory.map((item) => (
          <Link
            key={item.title}
            to={item.url}
            className={`flex items-center gap-2 py-2 hover:bg-orange-100 transition-colors text-sm group relative ${
              activeItem === item.url ? "bg-orange-50 text-orange-600 font-semibold" : "text-gray-700"
            } ${isCollapsed ? "px-4 justify-center" : "px-6"}`}
            onClick={() => setActiveItem(item.url)}
            title={isCollapsed ? item.title : ""}
          >
            <item.icon className="h-4 w-4 flex-shrink-0" />
            {!isCollapsed && <span className="whitespace-nowrap">{item.title}</span>}
            {isCollapsed && (
              <div className="absolute left-full ml-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                {item.title}
              </div>
            )}
          </Link>
        ))}

        {/* Sales Section */}
        {!isCollapsed && <div className="mt-4 mb-2 px-4 text-xs font-bold text-gray-500">Sales</div>}
        {navigationData.sales.map((item) => (
          <Link
            key={item.title}
            to={item.url}
            className={`flex items-center gap-2 py-2 hover:bg-orange-100 transition-colors text-sm group relative ${
              activeItem === item.url ? "bg-orange-50 text-orange-600 font-semibold" : "text-gray-700"
            } ${isCollapsed ? "px-4 justify-center" : "px-6"}`}
            onClick={() => setActiveItem(item.url)}
            title={isCollapsed ? item.title : ""}
          >
            <item.icon className="h-4 w-4 flex-shrink-0" />
            {!isCollapsed && <span className="whitespace-nowrap">{item.title}</span>}
            {isCollapsed && (
              <div className="absolute left-full ml-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                {item.title}
              </div>
            )}
          </Link>
        ))}

        {/* Purchases Section */}
        {!isCollapsed && <div className="mt-4 mb-2 px-4 text-xs font-bold text-gray-500">Purchases</div>}
        {navigationData.purchases.map((item) => (
          <Link
            key={item.title}
            to={item.url}
            className={`flex items-center gap-2 py-2 hover:bg-orange-100 transition-colors text-sm group relative ${
              activeItem === item.url ? "bg-orange-50 text-orange-600 font-semibold" : "text-gray-700"
            } ${isCollapsed ? "px-4 justify-center" : "px-6"}`}
            onClick={() => setActiveItem(item.url)}
            title={isCollapsed ? item.title : ""}
          >
            <item.icon className="h-4 w-4 flex-shrink-0" />
            {!isCollapsed && <span className="whitespace-nowrap">{item.title}</span>}
            {isCollapsed && (
              <div className="absolute left-full ml-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                {item.title}
              </div>
            )}
          </Link>
        ))}

        {/* Reports Section */}
        {!isCollapsed && <div className="mt-4 mb-2 px-4 text-xs font-bold text-gray-500">Reports</div>}
        {navigationData.reports.map((item) => (
          <Link
            key={item.title}
            to={item.url}
            className={`flex items-center gap-2 py-2 hover:bg-orange-100 transition-colors text-sm group relative ${
              activeItem === item.url ? "bg-orange-50 text-orange-600 font-semibold" : "text-gray-700"
            } ${isCollapsed ? "px-4 justify-center" : "px-6"}`}
            onClick={() => setActiveItem(item.url)}
            title={isCollapsed ? item.title : ""}
          >
            <item.icon className="h-4 w-4 flex-shrink-0" />
            {!isCollapsed && <span className="whitespace-nowrap">{item.title}</span>}
            {isCollapsed && (
              <div className="absolute left-full ml-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                {item.title}
              </div>
            )}
          </Link>
        ))}

        {/* Users Section */}
        {!isCollapsed && <div className="mt-4 mb-2 px-4 text-xs font-bold text-gray-500">Users</div>}
        {navigationData.users.map((item) => (
          <Link
            key={item.title}
            to={item.url}
            className={`flex items-center gap-2 py-2 hover:bg-orange-100 transition-colors text-sm group relative ${
              activeItem === item.url ? "bg-orange-50 text-orange-600 font-semibold" : "text-gray-700"
            } ${isCollapsed ? "px-4 justify-center" : "px-6"}`}
            onClick={() => setActiveItem(item.url)}
            title={isCollapsed ? item.title : ""}
          >
            <item.icon className="h-4 w-4 flex-shrink-0" />
            {!isCollapsed && <span className="whitespace-nowrap">{item.title}</span>}
            {isCollapsed && (
              <div className="absolute left-full ml-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                {item.title}
              </div>
            )}
          </Link>
        ))}

        {/* Settings Section */}
        {!isCollapsed && <div className="mt-4 mb-2 px-4 text-xs font-bold text-gray-500">Settings</div>}
        {navigationData.settings.map((item) => (
          item.type === 'btn' ? (
            <button
              key={item.title}
              onClick={handleLogout}
              className={`flex items-center gap-2 py-2 hover:bg-orange-100 transition-colors text-sm group relative ${
                activeItem === item.url ? "bg-orange-50 text-orange-600 font-semibold" : "text-gray-700"
              } ${isCollapsed ? "px-4 justify-center" : "px-6"}`}
              title={isCollapsed ? item.title : ""}
            >
              <item.icon className="h-4 w-4 flex-shrink-0" />
              {!isCollapsed && <span className="whitespace-nowrap">{item.title}</span>}
            </button>
          ) : (
            <Link
              key={item.title}
              to={item.url}
              className={`flex items-center gap-2 py-2 hover:bg-orange-100 transition-colors text-sm group relative ${
                activeItem === item.url ? "bg-orange-50 text-orange-600 font-semibold" : "text-gray-700"
              } ${isCollapsed ? "px-4 justify-center" : "px-6"}`}
              onClick={() => setActiveItem(item.url)}
              title={isCollapsed ? item.title : ""}
            >
              <item.icon className="h-4 w-4 flex-shrink-0" />
              {!isCollapsed && <span className="whitespace-nowrap">{item.title}</span>}
              {isCollapsed && (
                <div className="absolute left-full ml-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                  {item.title}
                </div>
              )}
            </Link>
          )
        ))}
      </nav>
    </aside>
  )
}

export default SideNavbar
